import { ImageProps } from '@msc-b2c/interfaces/types';
import clsx from 'clsx';
import './image.module.scss';
import { useEffect, useRef, useState } from 'react';
import { SkeletonLoader } from '../skeleton/Skeleton';
import { generateSrcSet } from '@msc-b2c/shared-utils';

export const Image = ({
  id,
  src,
  srcSet,
  sizes,
  loading = 'lazy',
  widths,
  qlt = 85,
  alt,
  styleOption = '',
  draggable,
  placeholderImagePath = '',
  priority,
  originalPath,
}: ImageProps) => {
  // Default widths for srcSet if not provided
  const defaultWidths = [320, 480, 600, 800, 1024, 1200, 1600, 1920, 2400];
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imageInError, setImageInError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setImagesLoaded(false);
    if (!src?.includes('cruise-image-loading')) setImageInError(false);

    // On SPA navigation the <img> can already be cached/complete, so `onLoad` never fires and the
    // image would stay hidden indefinitely. Detect the already-decoded image and reveal it.
    const imgElement = imgRef.current;
    if (imgElement?.complete && imgElement.naturalWidth > 0) {
      setImagesLoaded(true);
    } else {
      setImagesLoaded(false);
    }
  }, [src]);

  let fit = false;
  if (originalPath?.includes('/2d/')) {
    fit = true;
  }

  const shouldShowSkeleton = !imagesLoaded || src?.includes('cruise-image-loading');

  return (
    <div
      id={id}
      className={clsx('image', 'cmp-shared-image', styleOption)}
      data-image-loaded={imagesLoaded ? 'true' : 'false'}
    >
      {/* Show SkeletonLoader while image is loading */}
      {shouldShowSkeleton && (
        <SkeletonLoader
          width="100%"
          height="100%"
          styleOption="skeleton"
        />
      )}

      {/* Image element with ref to control visibility */}
      <div className={'cmp-image'}>
        <img
          ref={imgRef}
          src={src}
          {...{ fetchpriority: priority === 'high' ? 'high' : 'auto' }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = placeholderImagePath;
            setImageInError(true);
            setImagesLoaded(true);
          }}
          srcSet={
            imageInError
              ? generateSrcSet(placeholderImagePath, widths || defaultWidths, qlt)
              : srcSet || generateSrcSet(src, widths || defaultWidths, qlt)
          }
          sizes={sizes}
          alt={alt}
          draggable={!!draggable}
          loading={priority === 'high' ? 'eager' : loading}
          decoding="async"
          onLoad={() => setImagesLoaded(true)}
          title={alt ?? ''}
          {...(fit && { fit: 'true' })}
        />
      </div>
    </div>
  );
};
